/**
 * Captures Chrome's one-shot `beforeinstallprompt` as early as possible (imported eagerly from
 * features/caddy/routes.tsx, i.e. at app start), so the caddy install hint can offer one-tap
 * install even though the hint itself mounts later inside a lazily loaded route.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function deferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

export function appInstalled(): boolean {
  return installed;
}

/** Use (and forget) the captured prompt — Chrome allows prompt() only once per event. */
export function takeInstallPrompt(): BeforeInstallPromptEvent | null {
  const e = deferred;
  deferred = null;
  notify();
  return e;
}
